import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { getValidCartItems } from '../../../utils/cart';
import { DRAFT_CART_SCOPE } from '../draftCart/cartScope';

const selectAutoCombo = (state: RootState) => state.autoCombo;
const selectAutoComboCartItems = createSelector(
  selectAutoCombo,
  (state) => state.autoCombinedCartItems
);

const selectAutoComboCartItemsQuantity = createSelector(
  selectAutoCombo,
  (state) => state.autoCombinedCartItemsQuantity
);

const selectLastNonAutoCombinedCartItemId = createSelector(
  selectAutoCombo,
  (state) => {
    // HITL-1282: guard against an undefined autoCombinedCartItems slice and
    // return undefined when there is no preceding non-auto-combined item
    // (findIndex returning -1 or 0 used to underflow into items[-1].cartItemId
    // and throw "Cannot read properties of undefined").
    const items = Object.values(state.autoCombinedCartItems ?? {});
    const firstAutoCombinedCartItemIdx = items.findIndex(
      (item) =>
        item.autoCombinedCartItemIds && item.autoCombinedCartItemIds.length > 0
    );
    if (firstAutoCombinedCartItemIdx <= 0) return undefined;
    return items[firstAutoCombinedCartItemIdx - 1].cartItemId;
  }
);

/**
 * HITL-1610 — has the item the agent has open stopped being openable?
 *
 * The auto-combo recompute runs on EVERY cart change (the `useEffect` in
 * `OrderReview`), and both copies of it re-point the centre pane by asking
 * "where is the first combined item in the cart" — a fact about the cart's
 * shape, not about what the agent is looking at. The only reason to move the
 * agent's cursor is that what they have open can no longer be rendered, which is
 * exactly this predicate, lifted from `ItemBuilder`'s own condition
 * (`!cartItem.autoCombinedCartItemIds`).
 *
 * Two consequences worth knowing:
 * - No selection means nothing to rescue, so this is false. Under the flag the
 *   recompute then leaves the pane alone rather than making a selection of its
 *   own — which is the point in `Shadow`, where the pane is the agent's.
 * - It is false for BM-combined items, because the BM path marks them
 *   `isAutoCombined` (`orderSlice.bm.ts`) while `ItemBuilder` gates on
 *   `autoCombinedCartItemIds`, which only the PRP path sets (`menuSlice.ts`).
 *   A BM-combined item renders fine, so moving off it was never protecting
 *   anything — the BM re-point is the PRP rule copied with the marker swapped.
 *
 * - It is false for a My Cart selection (HITL-1715). Both carts number
 *   `cartItemId` from 1, so looking a draft id up here finds the AI's line of
 *   the same number — or none — and the recompute "rescued" the agent onto the
 *   AI's cart without a `cartScope`. Every keyboard edit after that landed in
 *   the Live Order. The draft has no combined lines and this recompute only
 *   ever runs over `cartSlice`, so a draft selection is never its to move.
 *
 * Read only under `hitl_shadow_mode`; with the flag off both recomputes run
 * their original block untouched.
 */
const selectIsSelectedItemUnopenable = createSelector(
  selectAutoComboCartItems,
  (state: RootState) => state.cart.cartItems,
  (state: RootState) => state.dialog.selectedItemCartId,
  (state: RootState) => state.dialog.selectedItemCartScope,
  (
    autoComboCartItems,
    cartItems,
    selectedItemCartId,
    selectedItemCartScope
  ) => {
    if (!selectedItemCartId) return false;
    if (selectedItemCartScope === DRAFT_CART_SCOPE) return false;
    // Mirrors `ItemBuilder`: the combined map wins whenever it is populated,
    // which it always is by the time either recompute reaches its Step-9 —
    // `setAutoCombinedCart` is dispatched first.
    const items = Object.keys(autoComboCartItems ?? {}).length
      ? autoComboCartItems
      : cartItems;
    const selected = items?.[selectedItemCartId];
    return !selected || !!selected.autoCombinedCartItemIds;
  }
);

const selectValidAutoComboCartItems = createSelector(
  selectAutoComboCartItems,
  (autoComboCartItems) => getValidCartItems(autoComboCartItems)
);

export {
  selectAutoComboCartItems,
  selectAutoComboCartItemsQuantity,
  selectValidAutoComboCartItems,
  selectLastNonAutoCombinedCartItemId,
  selectIsSelectedItemUnopenable,
};
